import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { getBackendUrl } from './backendUrl';
import { getToken, getRefreshToken, setToken, setRefreshToken, removeToken, removeRefreshToken } from '../utils/HelperFunctions';

// axios doesn't include `_retry` on its config types; we attach it after
// a failed auth refresh to prevent infinite retry loops.
type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshPromise: Promise<string> | null = null;

const shouldSkipAuthRefresh = (config: AxiosRequestConfig = {}) => {
  const url = config?.url || '';
  return config?.headers?.['x-skip-auth-refresh'] || url.includes('/users/login') || url.includes('/auth/refresh-token');
};

// Function to create the Axios instance
const createApiInstance = async () => {
  const baseURL = await getBackendUrl();

  const api = axios.create({
    baseURL,
  });

  const accessToken = getToken();
  if (accessToken) {
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  }

  api.interceptors.request.use(
    (config) => {
      const currentToken = getToken();
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = (error.config || {}) as RetryableRequestConfig;

      if (shouldSkipAuthRefresh(originalRequest)) {
        return Promise.reject(error);
      }

      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(error);
      }

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        removeToken();
        removeRefreshToken();
        localStorage.setItem('SSOLogin', 'false');
        delete api.defaults.headers.common['Authorization'];
        return Promise.reject(error);
      }

      try {
        if (!refreshPromise) {
          refreshPromise = api
            .post('/auth/refresh-token', { refreshToken }, { headers: { 'x-skip-auth-refresh': 'true' } })
            .then((response) => {
              const newToken = response?.data?.token;
              const newRefreshToken = response?.data?.refreshToken;

              if (!newToken || !newRefreshToken) {
                throw new Error('Invalid refresh response');
              }

              setToken(newToken);
              setRefreshToken(newRefreshToken);
              api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
              return newToken;
            })
            .finally(() => {
              refreshPromise = null;
            });
        }

        const newToken = await refreshPromise;
        originalRequest._retry = true;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        removeToken();
        removeRefreshToken();
        localStorage.setItem('SSOLogin', 'false');
        delete api.defaults.headers.common['Authorization'];
        return Promise.reject(refreshError);
      }
    }
  );

  console.log('api url: ' + baseURL);
  return api;
};

// Export the Axios instance
export const api = await createApiInstance();
export default api;
